import * as yup from 'yup';

import { getFlattenedFieldViolationErrors } from '@cast/api-client';
import {
  Button,
  Icons,
  Typography,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';
import { makeValidateSync } from '@cast/utils';

import { RffForm, RffInput } from '@cast/shared-rff-ui';

const validate = makeValidateSync(
  yup.object({
    clusterName: yup
      .string()
      .required('Cluster name is required')
      .max(63, 'Cluster name must not exceed 63 characters')
      .matches(
        /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
        'Cluster name must be RFC 1123 compliant: start and end with alphanumeric characters, contain only lowercase alphanumeric characters and hyphens.'
      ),
  })
);

type Props = {
  onGenerateScript: (name: string) => void;
  wasScriptGenerated: boolean;
  isScriptGenerating: boolean;
  initialAnywhereClusterName?: string;
  error: unknown;
};

export const CastAnywhereSection = ({
  onGenerateScript,
  wasScriptGenerated,
  isScriptGenerating,
  initialAnywhereClusterName,
  error,
}: Props) => {
  const parsedError = getFlattenedFieldViolationErrors(error, 'anywhere.');
  const clusterNameError = parsedError?.clusterName;

  return (
    <Stack
      borderBottom={tokens.theme.border.structural.neutralSubtle}
      mx={-24}
      px={24}
      pb={16}
    >
      <Typography variant="A1" mb={2}>
        Connect any Kubernetes cluster with internet access
      </Typography>
      <Typography variant="P12R" color="textSecondary" mb={16}>
        Connect any Kubernetes cluster with internet access, whether it&apos;s
        on-premises, self-hosted, or from other cloud provider.
      </Typography>
      <RffForm
        onSubmit={({ clusterName }: { clusterName: string }) =>
          onGenerateScript(clusterName)
        }
        initialValues={{ clusterName: initialAnywhereClusterName }}
        validate={validate}
        subscription={{ values: true }}
      >
        {({ values }) => (
          <RffInput
            name="clusterName"
            label="Enter cluster name:"
            size="small"
            disabled={isScriptGenerating}
            {...(!!clusterNameError && {
              error: clusterNameError,
              success: false,
            })}
            endOutsideAdornment={
              wasScriptGenerated ? (
                <Button
                  size="sm"
                  variant="text"
                  startIcon={<Icons.ArrowCounterClockwise />}
                  type="submit"
                  loading={isScriptGenerating}
                  disabled={!values.clusterName}
                  sx={{ ml: 16 }}
                >
                  Regenerate script
                </Button>
              ) : (
                <Button
                  size="sm"
                  type="submit"
                  loading={isScriptGenerating}
                  disabled={!values.clusterName}
                  sx={{ ml: 16 }}
                  testId="anywhere-generate-script-button"
                >
                  Generate script
                </Button>
              )
            }
            sx={{
              width: '100%',
              '& .DS-InputLabel-label': {
                typography: 'L10M',
                pb: 4,
                color: colorOrToken(
                  'grey.400',
                  tokens.theme.fg.structural.secondary
                ),
              },
            }}
            testId="anywhere-cluster-name-input"
          />
        )}
      </RffForm>
    </Stack>
  );
};
